import { t } from 'i18next'
import { cloneDeep } from 'lodash'
import React, { useState } from 'react'
import { Button, Col, Form, Row, Modal, Spinner } from 'react-bootstrap'
import { useArticleDataState, sizeTemplateObj } from '../../../../../stores/Configuration/ArticleDataProvider'
import { setValueByKey } from '../../../../../utils/utils'
import { ButtonWithLoadingState } from '../../../../Buttons'
import LanguageForm from '../../../../Forms/LanguageForm'
import LanguageNameInput from '../../../../Forms/LanguageNameInput'
import OverlayContainer from '../../../../Page/logix2020/OverlayContainer'
import OverlayRight from '../../../../Page/logix2020/OverlayRight'
import useMountEffect from '../../../../../utils/hooks/useMountEffect'
import { StyledValidationMessage } from '../../formHelper'
import InformationPopover from '../../../../InformationPopover/InformationPopover'
import { isEmptyGuid } from '../../../../../utils/emptyGuid'

const SizeModal = ({ handleClose, systemId, sizeId, editSize }) => {
    const { actionMode, articleBaseObj, getFormErrorByKey, resetFormErrorByKey, patchSize, postSize, loadingStates, resetStates } = useArticleDataState()
    const [overlayVisible, setOverlayVisible] = useState(false)
    const [size, setSize] = useState(null)

    useMountEffect(() => {
        resetStates()
        if (sizeId) {
            setSize(editSize)
        }
        else {
            let newSizeObj = cloneDeep(sizeTemplateObj)
            if (articleBaseObj && articleBaseObj.price) {
                newSizeObj.price = cloneDeep(articleBaseObj.price)
                newSizeObj.price.value = null
            }
            setSize(newSizeObj)
        }
    })

    const updateTranslations = (name) => (translations) => {
        setSize(prev => ({
            ...prev,
            [name]: {
                ...prev[name],
                values: translations
            }
        }))
        resetFormError(name)
    }
    const updateInputFields = (e) => {
        const { target } = e
        let key = target.name
        let value = null
        switch (key) {
            case "price":
                setSize(prev => ({
                    ...prev,
                    price: {
                        ...prev.price,
                        value: parseFloat(target.value) || null
                    }
                }))
                break;
            default:
                if (target.type === "checkbox") { value = target.checked }
                else { value = target.value }
                setSize(prev => (setValueByKey(key, value, prev)))
                break;
        }
        resetFormError(key)
    }
    const handleCloseOverlay = () => {
        setOverlayVisible(false)
    }
    const handleSave = () => {
        sizeId
            ? patchSize(systemId, sizeId, size, handleClose)
            : postSize(systemId, size, handleClose)
    }
    const resetFormError = (key) => {
        resetFormErrorByKey("saveSize", key)
    }
    const getFormError = (key) => {
        return getFormErrorByKey("saveSize", key)
    }
    const foreignKeyIsEnabled = () => {
        return actionMode === 'copy' || size.isNewPosition || !size.foreignKey || isEmptyGuid(sizeId)
    }
    return (
        <div>
            {/* enforceFocus for OverlayRight with overLayZIndex with inputs  */}
            <Modal show={true} size="lg" onHide={handleClose} enforceFocus={false}>
                <Modal.Header closeButton>
                    <Modal.Title>{sizeId ? t('sizeAdmin:editSize') : t('sizeAdmin:addSize')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {(loadingStates.getSizes || !size)
                        ? <div className="d-flex align-items-center justify-content-center">< Spinner animation='border' variant='primary' /></div >
                        : <Form>
                            <Row className="mb-3">
                                <Col>
                                    <LanguageNameInput
                                        id={`${systemId}_${sizeId}name`}
                                        openLanguageModal={() => setOverlayVisible(true)}
                                        languageArray={size.name.values}
                                        onChange={updateTranslations("name")}
                                        label={t('sizeAdmin:name')}
                                        isInvalid={!!getFormError("name")}
                                    />
                                    {(!!getFormError("name")) &&
                                        <StyledValidationMessage>{getFormError("name")}</StyledValidationMessage>
                                    }
                                </Col>
                                <Col>
                                    <Form.Group>
                                        <Form.Label>{t('costCenterAdmin:foreignKey')}{foreignKeyIsEnabled() && <InformationPopover header={t('costCenterAdmin:foreignKey')} content={t('officeAdmin:noteForeignKey')} />}</Form.Label>
                                        <Form.Control
                                            id={`${systemId}_${sizeId}foreignKey`}
                                            name="foreignKey"
                                            placeholder={t('costCenterAdmin:foreignKey')}
                                            value={size.foreignKey}
                                            disabled={!foreignKeyIsEnabled()}
                                            onChange={updateInputFields}
                                            isInvalid={!!getFormError("foreignKey")}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {getFormError("foreignKey")}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className='mb-3'>
                                <Col>
                                    <Form.Group>
                                        <Form.Label>{t('sizeAdmin:supplierAlias')}</Form.Label>
                                        <Form.Control
                                            id={`${systemId}_${sizeId}supplierAlias`}
                                            name="supplierAlias"
                                            placeholder={t('sizeAdmin:supplierAlias')}
                                            value={size.supplierAlias ?? ''}
                                            onChange={updateInputFields}
                                            isInvalid={!!getFormError("supplierAlias")}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {getFormError("supplierAlias")}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group>
                                        <Form.Label>{`${t('sizeAdmin:pricePerItem')} (${(size.price?.currency?.isoCode ?? "")})`}</Form.Label>
                                        <Form.Control
                                            id={`${systemId}_${sizeId}price`}
                                            value={size.price?.value ?? ""}
                                            name="price"
                                            type="number"
                                            min="0"
                                            onWheel={(e) => e.target.blur()}
                                            onChange={updateInputFields}
                                            isInvalid={!!getFormError("price")}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {getFormError("price")}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                            {/*<Row className='mb-3'>
                                <Col>
                                    <Form.Group>
                                        <Form.Label>{`${t('sizeAdmin:pricePerItem')} (${(size.price?.currency?.isoCode ?? "")})`}</Form.Label>
                                        <Form.Control
                                            id={`${systemId}_${sizeId}price`}
                                            value={size.price?.value??""}
                                            name="price"
                                            type="number"
                                            min="0"
                                            onWheel={(e) => e.target.blur()}
                                            onChange={updateInputFields}
                                            isInvalid={!!getFormError("price")}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {getFormError("price")}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col>
                                </Col>
                            </Row>*/}
                            <Row className="mb-3">
                                <Col>
                                    <Form.Check
                                        type="checkbox"
                                        id={`${systemId}_${sizeId}isObsolet`}
                                        name="isObsolet"
                                        label={t('sizeAdmin:isObsolet')}
                                        checked={size.isObsolet}
                                        onChange={updateInputFields}
                                        isInvalid={!!getFormError("isObsolet")}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {getFormError("isObsolet")}
                                    </Form.Control.Feedback>
                                </Col>
                                <Col>
                                    <Form.Check
                                        type="checkbox"
                                        id={`${systemId}_${sizeId}isLocked`}
                                        name="isLocked"
                                        label={t('sizeAdmin:isLocked')}
                                        checked={size.isLocked}
                                        onChange={updateInputFields}
                                        isInvalid={!!getFormError("isLocked")}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {getFormError("isLocked")}
                                    </Form.Control.Feedback>
                                </Col>
                            </Row>
                        </Form>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        {t('cancelButton')}
                    </Button>
                    <ButtonWithLoadingState
                        disabled={false}
                        buttonText={t('saveButton')}
                        isLoading={loadingStates.saveSize}
                        onClick={handleSave}
                    />
                </Modal.Footer>
            </Modal>
            <OverlayContainer>
                <OverlayRight visible={overlayVisible} overLayZIndex={1056} onClose={handleCloseOverlay}>
                    <LanguageForm
                        languageTitle={t('sizeAdmin:name')}
                        handleCloseOverlay={handleCloseOverlay}
                        languageArray={size ? size.name.values : []}
                        onChange={updateTranslations("name")}
                    />
                </OverlayRight>
            </OverlayContainer>
        </div>
    )
}

export default SizeModal